import type { ThemeConfig } from "antd";
import { theme as antThemes } from "antd";
import enUS from "antd/locale/en_US";
import trTr from "antd/locale/tr_TR";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTernaryDarkMode } from "usehooks-ts";

import { getAntDarkTheme } from "@/configs/themes/antTheme/dark";
import { getAntLightTheme } from "@/configs/themes/antTheme/light";
import { getDarkStyledTheme } from "@/configs/themes/styledTheme/dark";
import { getLightStyledTheme } from "@/configs/themes/styledTheme/light";

import { LANGUAGES } from "./constants/i18next.constants";
import { useColorScheme } from "./hooks/useColorScheme";

export const useGetMyTheme = () => {
  const { isDarkMode } = useTernaryDarkMode();
  const colorScheme = useColorScheme();

  // Note: Theme can set by user preference
  const themes = useMemo(() => {
    return {
      dark: getDarkStyledTheme(colorScheme),
      light: getLightStyledTheme(colorScheme),
    };
  }, [colorScheme]);

  return useMemo(() => {
    return isDarkMode
      ? themes.dark
      : themes.light;
  }, [isDarkMode, themes]);
};

export const useGetAntTheme = (): ThemeConfig => {
  const { isDarkMode } = useTernaryDarkMode();
  const colorScheme = useColorScheme();

  const themes = useMemo(() => {
    return {
      dark: getAntDarkTheme(colorScheme),
      light: getAntLightTheme(colorScheme),
    };
  }, [colorScheme]);

  return useMemo(() => {
    return {
      ...isDarkMode
        ? themes.dark
        : themes.light,
      algorithm: isDarkMode
        ? antThemes.darkAlgorithm
        : antThemes.defaultAlgorithm,
    };
  }, [isDarkMode, themes]);
};

export const useAntLocale = () => {
  const { i18n } = useTranslation();

  const antLocale = useMemo(() => {
    if (i18n.language === LANGUAGES.EN) {
      return enUS;
    }
    return trTr;
  }, [i18n.language]);

  return antLocale;
};
